import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Upload,
  Typography,
  Select,
} from 'antd';
import ImgCrop from 'antd-img-crop';
import {
  UserOutlined,
  UploadOutlined,
  MailOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import PhoneInputUS from '../../common/PhoneInputUS';
import useProfileStore from '../../../stores/useProfileStore';
import { avatarPresetNames, getPresetAvatarSrc } from '../../../utils/avatar';
import { languageOptions } from '../../../utils/languages';
import { phone as phoneLib } from 'phone';

const { Text } = Typography;

const getSvgSrc = (name) => {
  try {
      const imported = require(`../../../assets/avatars/${name}.svg`);
      return typeof imported === 'string' ? imported : imported.default || imported;
  } catch (e) {
      return '';
  }
};

const presetAvatars = avatarPresetNames.map(name => ({
  name,
  src: getSvgSrc(name)
}));

function normalizeMobileValue(raw) {
  if (typeof raw === 'object' && raw !== null) {
    const { countryCode, areaCode, phoneNumber } = raw;
    if (!areaCode && !phoneNumber) return '';
    return `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
  }
  return typeof raw === 'string' ? raw : '';
}

const mobileValidator = (_, value) => {
  const mobile = normalizeMobileValue(value);
  if (!mobile) return Promise.resolve();
  const result = phoneLib(mobile);
  if (!result.isValid) return Promise.reject('Please enter a valid mobile number');
  return Promise.resolve();
};

const ProfileTab = ({ form, messageApi }) => {
  const {
    loading, avatarUrl, initials,
    avatarSaving, uploading, showPresets,
    uploadUserAvatar, selectAvatarPreset, saveProfile, setShowPresets
  } = useProfileStore();

  const handleAvatarSelect = (name, url) => selectAvatarPreset(name, url, messageApi);
  const handleAvatarUpload = (file) => uploadUserAvatar(file, messageApi);
  const handleSaveProfileWrapper = () => saveProfile(form, messageApi);

  const renderAvatar = () => {
    // Resolve URL if it's a preset name
    const resolvedAvatarUrl = getPresetAvatarSrc(avatarUrl);
    const avatarStyle = {
      border: '3px solid #4CAF50',
      borderRadius: '50%',
      padding: 3,
      display: 'inline-block',
      backgroundColor: '#FAFAFA',
      aspectRatio: '1 / 1',
      width: 90,
      height: 90,
      overflow: 'hidden'
    };

    const imgStyle = {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      objectFit: 'cover',
      display: 'block',
      aspectRatio: '1 / 1'
    };

    const avatarContent = resolvedAvatarUrl ? (
      <div style={avatarStyle}>
        <img src={resolvedAvatarUrl} alt="Avatar" style={imgStyle} />
      </div>
    ) : (
      <div style={avatarStyle}>
        <div style={{
          ...imgStyle,
          backgroundColor: '#4CAF50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          color: 'white',
          fontWeight: 600
        }}>
          {initials}
        </div>
      </div>
    );

    if (avatarSaving) {
      return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          {avatarContent}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            borderRadius: '50%'
          }}>
            <span style={{ fontSize: 12 }}>Saving...</span>
          </div>
        </div>
      );
    }

    return avatarContent;
  };

  return (
    <div style={{ padding: '0 40px', minHeight: '100%' }}>
      <div style={{ maxWidth: 800, padding: '32px 0' }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 24px 0', color: '#263238' }}>My Profile</h3>
        
        {/* Compact Avatar Module */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 32, padding: 24, border: '1px solid #E6E6E6', borderRadius: 8, backgroundColor: '#FAFAFA' }}>
           <div style={{ marginRight: 24 }}>
              {renderAvatar()}
           </div>
           <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4, color: '#263238' }}>Profile Picture</Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                Upload a new avatar or choose from our presets.
              </Text>
              
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                 <ImgCrop cropShape="round" showGrid={false} quality={1}>
                    <Upload
                      beforeUpload={(file) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => {
                          const img = new Image();
                          img.src = reader.result;
                          img.onload = () => {
                            const canvas = document.createElement('canvas');
                            const size = 124;
                            canvas.width = size;
                            canvas.height = size;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0, size, size);
                            canvas.toBlob((blob) => {
                              const resizedFile = new File([blob], file.name, { type: 'image/png' });
                              handleAvatarUpload(resizedFile);
                            }, 'image/png', 1);
                          };
                        };
                        return false;
                      }}
                      showUploadList={false} 
                      accept="image/*" 
                      disabled={uploading}
                    >
                       <Button icon={<UploadOutlined />} loading={uploading}>Upload New</Button>
                    </Upload>
                 </ImgCrop>
                 <Button onClick={() => setShowPresets(!showPresets)}>
                   {showPresets ? 'Hide Presets' : 'Choose Preset'}
                 </Button>
              </div>

              {showPresets && (
                <div style={{ marginTop: 16, borderTop: '1px solid #e6e6e6', paddingTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>Select a preset:</Text>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', gap: 8 }}>
                     {presetAvatars.map(avatar => (
                       <div
                         key={avatar.name}
                         onClick={() => !avatarSaving && handleAvatarSelect(avatar.name, avatar.src)}
                         style={{
                           cursor: avatarSaving ? 'wait' : 'pointer',
                           border: avatarUrl === avatar.src ? '2px solid #4CAF50' : '1px solid #E6E6E6',
                           borderRadius: '50%',
                           padding: 2,
                           backgroundColor: '#fff',
                           opacity: avatarSaving ? 0.6 : 1,
                           aspectRatio: '1/1',
                           overflow: 'hidden'
                         }}
                       >
                         <img src={avatar.src} alt={avatar.name} style={{ width: '100%', height: '100%', borderRadius: '50%', display: 'block', objectFit: 'cover' }} />
                       </div>
                     ))}
                  </div>
                </div>
              )}
           </div>
        </div>

        <Form form={form} layout="vertical">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} placeholder="Full Name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nickname" label="Nickname">
                  <Input placeholder="Nickname" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} placeholder="Username" disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                  <Input prefix={<MailOutlined />} placeholder="Email" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                   name="mobile"
                   label="Mobile"
                   rules={[{ validator: mobileValidator }]}
                   tooltip={form.getFieldValue('tfa_option') ? "2FA is enabled, to edit disable 2FA first" : null}
                >
                  <PhoneInputUS disabled={form.getFieldValue('tfa_option')} enableSearch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="jobTitle" label="Job Title">
                  <Input placeholder="Job Title" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="language"
                  label="Language"
                  tooltip="Messages in your inbox are shown in this language, and replies you write are translated from it when AI translation is enabled for your company."
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={languageOptions(form.getFieldValue('language'))}
                    suffixIcon={<TranslationOutlined />}
                    placeholder="English"
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Button
                 onClick={handleSaveProfileWrapper}
                 loading={loading}
                 type="primary"
                 style={{ backgroundColor: '#4CAF50', borderColor: '#4CAF50' }}
              >
                Save Profile
              </Button>
            </div>
        </Form>
      </div>
    </div>
  );
};

export default ProfileTab;
